import { useState, useEffect } from "react";
import axios from "axios";
import ReportsTable from "../components/ReportsTable.jsx";
import ReportsModal from "../components/ReportsModal.jsx";
import Filters from "../components/Filters.jsx";
import Bottom from "../components/Bottom.jsx";
import "../css/EvidenceMap.css";
import { Typography } from "@mui/material";

function EvidenceMap() {
	const [dataRaw, setDataRaw] = useState();
	const [axisLabels, setAxisLabels] = useState();
	const [cleanData, setCleanData] = useState();
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [currentReports, setCurrentReports] = useState([]);
	const [filters, setFilters] = useState();
	const [filtersSelected, setFiltersSelected] = useState();
	const [isCheckAllFilters, setIsCheckAllFilters] = useState();

	const selectedXAxisTitle = "type_of_intervention";
	const selectedYAxisTitle = "outcomes";
	const [shouldRenderTable, setShouldRenderTable] = useState(false); //for the issue rendering table before setting new axis labels

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		setShouldRenderTable(false);
		axios({
			url: "/evidence-map/data",
		})
			.then((response) => {
				!dataRaw && setDataRaw(response.data);
				const uniqueXlabels = [
					...new Set(
						response.data.map((item) => item[selectedXAxisTitle])
					),
				];
				uniqueXlabels.sort();
				const indexOtherX = uniqueXlabels.indexOf("Other");
				uniqueXlabels.push(uniqueXlabels.splice(indexOtherX, 1)[0]);
				const uniqueYlabels = [
					...new Set(
						response.data.map((item) => item[selectedYAxisTitle])
					),
				];
				uniqueYlabels.sort();
				const indexOtherY = uniqueYlabels.indexOf("Other");
				uniqueYlabels.push(uniqueYlabels.splice(indexOtherY, 1)[0]);
				setAxisLabels({
					x: uniqueXlabels,
					y: uniqueYlabels,
				});
			})
			.catch((error) => {
				if (error.response) {
					console.log(error.response);
					console.log(error.response.status);
					console.log(error.response.headers);
				}
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (filters) {
			setFiltersSelected(filters);
			const initialCheckAllState = {};
			Object.keys(filters).forEach((filterName) => {
				initialCheckAllState[filterName] = true;
			});
			setIsCheckAllFilters(initialCheckAllState);
		}
	}, [filters]);

	useEffect(() => {
		const transformData = () => {
			const dataMap = {};
			axisLabels.x.forEach((intervention) => {
				dataMap[intervention] = {};
				axisLabels.y.forEach((outcome) => {
					dataMap[intervention][outcome] = [];
				});
			});

			dataRaw.forEach((item) => {
				const intervention = item[selectedXAxisTitle];
				const outcomes = item[selectedYAxisTitle];
				const reportName = item["title"];

				dataMap[intervention][outcomes].push(reportName);
			});
			setShouldRenderTable(true);
			return dataMap;
		};
		if (dataRaw !== undefined && typeof axisLabels !== "undefined") {
			setCleanData(transformData());
		}
	}, [dataRaw, axisLabels]);

	const handleShowReports = (Intervention, Condition) => {
		const reports = cleanData[Intervention][Condition];
		const reportInfo = [];

		reports.forEach((reportName) => {
			const foundReport = dataRaw.find(
				(report) => report.title === reportName
			);

			if (foundReport) {
				const reportIndex = dataRaw.indexOf(foundReport);
				const abstract = foundReport.abstract;
				const title = foundReport.title;
				const year = foundReport.year_of_publication;
				const source = foundReport.source;
				const authors = foundReport.authors;
				reportInfo.push({
					index: reportIndex,
					title: title,
					abstract: abstract,
					year: year,
					authors: authors,
					source: source,
				});
			}
		});

		setCurrentReports(reportInfo);
		setModalIsOpen(true);
	};

	const closeModal = () => {
		setModalIsOpen(false);
		setCurrentReports([]);
	};

	useEffect(() => {
		if (filtersSelected !== undefined) {
			axios({
				method: "POST",
				url: "/evidence-map/filtered-data",
				data: { filtersSelected },
			})
				.then((response) => {
					setDataRaw(response.data);
				})
				.catch((error) => {
					if (error.response) {
						console.log(error.response);
						console.log(error.response.status);
						console.log(error.response.headers);
					}
				});
		}
	}, [filtersSelected]);

	useEffect(() => {
		axios({
			url: "/evidence-map/filters",
		})
			.then((response) => {
				const filtersTemp = Object.fromEntries(
					Object.entries(response.data)
				);
				filtersTemp["Year of publication"].sort();
				setFilters(filtersTemp);
			})
			.catch((error) => {
				if (error.response) {
					console.log(error.response);
					console.log(error.response.status);
					console.log(error.response.headers);
				}
			});
	}, []);

	return (
		<div className="evidence-map-container">
			<div className="introduction-text-container">
				<div className="introduction-text-title">Evidence Map</div>
				<div className="introduction-text-description">
					<Typography
						variant="body1"
						sx={{ fontSize: 18, fontWeight: "bold" }}
					>
						We carried out a review of the evidence to understand
						what initiatives and approaches have worked to address
						social isolation among individuals experiencing
						homelessness and substance use problems. Here you can
						read a{" "}
						<span
							style={{
								textDecoration: "underline",
								color: "#651e7a",
							}}
						>
							<a href="/download/Rapid Evidence Assessment - summary.pdf">
								short summary
							</a>
						</span>{" "}
						of our research or the{" "}
						<span
							style={{
								textDecoration: "underline",
								color: "#651e7a",
							}}
						>
							<a href="/download/Rapid Evidence Assessment.pdf">
								full research report
							</a>
						</span>{" "}
						and explore an interactive evidence map of the evidence
						we gathered.
					</Typography>
				</div>
			</div>
			<div className="primary-filters-container">
				{filters && filtersSelected && (
					<Filters
						filters={filters}
						filtersSelected={filtersSelected}
						isCheckAllFilters={isCheckAllFilters}
						setFiltersSelected={setFiltersSelected}
						setIsCheckAllFilters={setIsCheckAllFilters}
						showClearAllButton={true}
					/>
				)}
			</div>
			{axisLabels && cleanData ? (
				<div className="table-container">
					<ReportsModal
						isOpen={modalIsOpen}
						onClose={closeModal}
						reports={currentReports}
					/>
					{shouldRenderTable && (
						<>
							<ReportsTable
								xAxisLabels={axisLabels.x}
								yAxisLabels={axisLabels.y}
								selectedXAxisTitle={"Type of Intervention"}
								selectedYAxisTitle={"Outcomes"}
								cleanData={cleanData}
								handleShowReports={handleShowReports}
							/>
						</>
					)}

					<Bottom />
				</div>
			) : (
				<div style={{ height: "calc(100vh - 519px" }} />
			)}
		</div>
	);
}

export default EvidenceMap;
